import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, Pill } from '@/shared/components';
import { toApiError, useApi, useApiProblem, type OnboardingLinkStatus } from '@/shared/api';
import { isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { OnboardingGuestLinkButton } from '@/features/calendar/OnboardingGuestLinkButton';
import { onboardingErrorText } from '@/features/system/onboardingErrors';
import { onboardingStatusLabel } from '@/features/system/systemLabels';
import { OnboardingLinkUsage, OnboardingMailSteps } from './OnboardingMailSteps';
import { SectionCard } from './SectionCard';
import { ShareOnboardingLinkPanel } from './ShareOnboardingLinkPanel';

function statusVariant(status: string): 'accent' | 'error' | 'outline' {
  if (status === 'Expired') return 'error';
  if (status === 'Sent' || status === 'Used') return 'accent';
  return 'outline';
}

/**
 * Gastzugang der Buchung: Begrüßung und Erinnerungen, Link öffnen, für Admins neu senden und
 * weitergeben. Nach der Abreise begrüßt niemand mehr neu – das lehnt auch der Server ab.
 */
export function OnboardingMailsCard({
  bookingId,
  propertyId,
  departed = false,
}: {
  bookingId: string;
  propertyId: string;
  departed?: boolean;
}) {
  const api = useApi();
  const { user } = useAuth();
  const canReissue = isWorkspaceAdmin(user);
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['onboardingSupport', bookingId, propertyId],
    queryFn: () => api.calendar.getOnboardingSupport(bookingId, propertyId),
  });
  const problem = useApiProblem(error);

  const reIssue = useMutation({
    mutationFn: () => api.system.onboardingLinkReIssue(bookingId),
    onSuccess: async () => {
      setConfirm(false);
      toast.success('Die Begrüßung liegt im Ausgang.');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['system', 'onboardingLinks'] }),
        qc.invalidateQueries({ queryKey: ['system', 'pendingOnboardings'] }),
        qc.invalidateQueries({ queryKey: ['onboardingSupport', bookingId, propertyId] }),
      ]);
    },
    onError: async (err) => {
      const problem = await toApiError(err);
      toast.error(onboardingErrorText(problem, 'Die Begrüßung konnte nicht gesendet werden.'));
      // Über Mitternacht offen geblieben: neu laden, damit der Knopf dem Hinweis weicht.
      if (problem.title === 'booking_stay_completed')
        await qc.invalidateQueries({ queryKey: ['bookings', 'detail', bookingId] });
    },
  });

  const link = data?.mail ?? null;
  const hasLink = link?.hasOnboardingLink === true;

  return (
    <SectionCard
      icon="Mail"
      title="Onboarding & E-Mails"
      trailing={
        hasLink && link ? (
          <Pill variant={statusVariant(link.linkStatus)} size="sm">
            {onboardingStatusLabel(link.linkStatus as OnboardingLinkStatus)}
          </Pill>
        ) : undefined
      }
    >
      {isLoading ? (
        <p className="text-ink-soft text-sm">Gastzugang wird geladen …</p>
      ) : isError ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-sealwax min-w-0 flex-1">
            {problem
              ? onboardingErrorText(problem, 'Onboarding-Status konnte nicht geladen werden.')
              : 'Onboarding-Status konnte nicht geladen werden.'}
          </span>
          <Button
            variant="ghost"
            className="min-h-11"
            loading={isFetching}
            onClick={() => void refetch()}
          >
            Erneut laden
          </Button>
        </div>
      ) : !hasLink || !link ? (
        <p className="text-ink-soft text-sm">
          Noch kein Onboarding-Link. Er entsteht automatisch, sobald die Buchung bestätigt und ein
          Gastprofil angelegt ist.
        </p>
      ) : (
        <>
          <OnboardingMailSteps link={link} />
          <OnboardingLinkUsage link={link} />
        </>
      )}

      <div className="mt-4 space-y-2">
        {hasLink && <OnboardingGuestLinkButton bookingId={bookingId} propertyId={propertyId} />}
        {canReissue && (
          <>
            {departed ? (
              <p className="text-ink-soft text-xs">
                Der Gast ist abgereist – eine neue Begrüßung geht nicht mehr.
              </p>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                leftIcon={<Send size={13} strokeWidth={2} />}
                onClick={() => setConfirm(true)}
                loading={reIssue.isPending}
              >
                Begrüßung neu senden
              </Button>
            )}
            <ShareOnboardingLinkPanel bookingId={bookingId} />
          </>
        )}
      </div>

      {canReissue && !departed && (
        <ConfirmDialog
          open={confirm}
          onOpenChange={setConfirm}
          title="Begrüßung neu senden?"
          description="Der Gast bekommt die Begrüßung noch einmal, mit einem neu ausgestellten Check-in-Link – per E-Mail oder, bei Kanalbuchungen, als Nachricht im Buchungsportal. Bisherige Links bleiben gültig."
          confirmLabel="Neu senden"
          variant="brand"
          loading={reIssue.isPending}
          onConfirm={() => reIssue.mutate()}
        />
      )}
    </SectionCard>
  );
}
